import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import basketSelector from 'selectors/basket/basketSelector';
import Actions from 'actions/Actions';
import basketConsts from 'constants/Basket';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import skuUtils from 'utils/Sku';
import SameDayUnlimitedBindings from 'analytics/bindingMethods/pages/myAccount/SameDayUnlimitedBindings';
import stringUtils from 'utils/String';
import localeUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';

import { showReorderFulfillmentOptionsPdpSelector } from 'viewModel/selectors/testTarget/showReorderFulfillmentOptionsPdpSelector';
import { showReorderFulfillmentOptionsSelector } from 'viewModel/selectors/testTarget/showReorderFulfillmentOptionsSelector';
import { isUserSDUSubscriberSelector } from 'viewModel/selectors/user/isUserSDUSubscriberSelector';
import { isUserSDUTrialEligibleSelector } from 'viewModel/selectors/user/isUserSDUTrialEligibleSelector';
import { showAddDeliveryPriceToSDDSelector } from 'viewModel/selectors/testTarget/showAddDeliveryPriceToSDDSelector';
import { activeFreeShippingCopyPDPSelector } from 'viewModel/selectors/testTarget/activeFreeShippingCopyPDPSelector';
import { isMultiStoreFilteringEnabledSelector } from 'viewModel/selectors/shopYourStore/isMultiStoreFilteringEnabledSelector';
import { isSDUAddedToBasketSelector } from 'viewModel/selectors/basket/isSDUAddedToBasketSelector';
import { isSameDayCouponAppliedSelector } from 'viewModel/selectors/basket/isSameDayCouponAppliedSelector';
import Location from 'utils/Location';
import urlUtils from 'utils/Url';
import { QUERY_PARAMS } from 'constants/Shared';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US.js';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA.js';

const { wrapHOC } = FrameworkUtils;
const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const { getLocalisedDataFromResource, getTextFromResource } = localeUtils;
const { DELIVERY_OPTIONS } = basketConsts;
const { showMediaModal, showSignInModal } = Actions;
const { isNDDEnabled, isNDDNewDeliveryName, getFastDeliveryTileSummarySubtitle } = DeliveryOptionsUtils;

const getText = getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

// Helper: Get delivery pricing text for A/B test (TS-4006)
const getDeliveryPricingText = (
    showDeliveryPrice,
    sameDayAvailable,
    currentSku,
    sddTilePromoMessage,
    isUserSDUSubscriber,
    isSDUAddedToBasket,
    isSameDayCouponApplied
) => {
    const displayDeliveryPrice = showDeliveryPrice && sameDayAvailable && currentSku?.isSameDayEligibleSku;

    if (!displayDeliveryPrice) {
        return null;
    }

    const shouldHidePricing = !!sddTilePromoMessage || isUserSDUSubscriber || isSDUAddedToBasket || isSameDayCouponApplied;

    if (shouldHidePricing) {
        return null;
    }

    // Use localized pricing strings based on country
    const isCanada = localeUtils.isCanada();
    const pricingKey = isCanada ? 'sduPricingCA' : 'sduPricingUS';

    return getText(pricingKey);
};

const sameDaySubTitleSelector = createSelector(
    (_state, ownProps) => ownProps.currentProduct,
    (_state, ownProps) => ownProps.sameDayAvailable,
    isUserSDUSubscriberSelector,
    isUserSDUTrialEligibleSelector,
    showAddDeliveryPriceToSDDSelector,
    isSDUAddedToBasketSelector,
    isSameDayCouponAppliedSelector,
    (product, sameDayAvailable, isUserSDUSubscriber, isUserSDUTrialEligible, showDeliveryPrice, isSDUAddedToBasket, isSameDayCouponApplied) => {
        const sddTilePromoMessage = product?.currentProductUserSpecificDetails?.sddTilePromoMessage;
        const { currentSku } = product;
        let subtitle = '';

        // A/B Test: Delivery pricing (TS-4006)
        const pricingText = getDeliveryPricingText(
            showDeliveryPrice,
            sameDayAvailable,
            currentSku,
            sddTilePromoMessage,
            isUserSDUSubscriber,
            isSDUAddedToBasket,
            isSameDayCouponApplied
        );

        if (pricingText) {
            return pricingText;
        }

        // Priority 1: Promo message takes precedence (existing logic)
        if (sddTilePromoMessage) {
            const isUserElegibleForSDDPromo = sameDayAvailable && (!isUserSDUSubscriber || isUserSDUTrialEligible);

            if (isUserElegibleForSDDPromo) {
                return sddTilePromoMessage;
            }
        }

        // Priority 2: SLA subtitle (NEW - GUAR-7594)
        // CRITICAL: Only show SLA subtitle when:
        // 1. isNDDEnabled() kill switch is ON
        // 2. User is NOT an active SDU subscriber (or is trial eligible)
        // Per Figma: SDU subscribers see blank tiles in non-promo state
        if (isNDDEnabled()) {
            const isUserElegibleForSLA = sameDayAvailable && (!isUserSDUSubscriber || isUserSDUTrialEligible);

            if (isUserElegibleForSLA) {
                const slaSubtitle = getFastDeliveryTileSummarySubtitle(currentSku, getText);

                if (slaSubtitle) {
                    return slaSubtitle;
                }
            }
        }

        // Priority 3: Not available message (existing logic)
        if (!currentSku.isSameDayEligibleSku) {
            subtitle = getText(isNDDEnabled() ? 'fastDeliveryNotAvailable' : 'sameDayDeliveryNotAvailable');
        }

        return subtitle;
    }
);

/* eslint-disable complexity */
const fields = createSelector(
    showReorderFulfillmentOptionsSelector,
    basketSelector,
    isSDUAddedToBasketSelector,
    userSubscriptionsSelector,
    (_state, ownProps) => ownProps.currentProduct,
    preferredStoreInfoSelector,
    (_state, ownProps) => ownProps.serviceUnavailable,
    createStructuredSelector({
        autoReplenishSubTitle: getTextFromResource(getText, 'autoReplenishSubTitle', ['{0}']),
        autoReplenishTitle: getTextFromResource(getText, 'autoReplenishTitle'),
        outOfStockAtStore: () => vars => getTextFromResource(getText, 'outOfStockAtStore', vars)()
    }),
    shipToHomeSelector,
    showReorderFulfillmentOptionsPdpSelector,
    sameDaySubTitleSelector,
    activeFreeShippingCopyPDPSelector,
    isMultiStoreFilteringEnabledSelector,
    (
        reorderFulfillmentOptions,
        basket,
        isSDUAddedToBasket,
        userSubscriptions,
        product,
        preferredStoreInfo,
        serviceUnavailable,
        textResources,
        shipToHome,
        reorderFulfillmentOptionsPdp,
        sameDaySubTitle,
        isFreeShippingCopyPDPActive,
        isMultiStoreFilteringEnabled
    ) => {
        const { currentSku } = product;
        const {
            isSameDayEligibleSku, sameDayTitle, replenishmentAdjuster = '0', acceleratedPromotion, isOutOfStock
        } = currentSku;
        const shouldDisplayEdd = !isOutOfStock;
        const isOnlineOnly = skuUtils.isOnlineOnly(currentSku);
        const isGiftCard = skuUtils.isGiftCard(currentSku);
        const bopisTitle = isGiftCard ? getText('availableInAllStores') : isOnlineOnly ? getText('onlineOnly') : getText('buyOnlineAndPickUp');
        const storeName = preferredStoreInfo?.displayName;
        const bopisSubTitle = !currentSku.isPickUpEligibleSku ? getText('pickUpNotOfferedForItem') : storeName;
        const pickupMessage = product.pickupMessage;

        const hasPickupMessage = Boolean(
            pickupMessage?.includes(getText('readyTomorrow')) ||
                pickupMessage?.includes(getText('readyToday')) ||
                pickupMessage?.includes(getText('getItToday')) ||
                pickupMessage?.includes('recevez aujourd’hui')
        );

        const bopisCutoffSubTitle = pickupMessage?.includes(getText('readyTomorrow'))
            ? getText('getItTomorrow')
            : hasPickupMessage
                ? getText('readyWithinTwoHours')
                : bopisSubTitle;

        const isCustomSets = !!(skuUtils.isCustomSetsSingleSkuProduct(product) || skuUtils.isCustomSetsGroupedSkuProduct(product));
        const displayBopis = Sephora.configurationSettings.isBOPISEnabled;
        const shippingMethodNotAvailable =
            isGiftCard || isOnlineOnly || isCustomSets || skuUtils.isGwp(currentSku) || skuUtils.isSample(currentSku) || !isSameDayEligibleSku;
        const sduSubscriptions = userSubscriptions?.filter(subscription => subscription.type === 'SDU');
        const isUserSduTrialEligible = sduSubscriptions.length > 0 ? sduSubscriptions[0]?.isTrialEligible : true;
        const isReplenishmentEligible = currentSku?.isReplenishmentEligible;
        const autoReplenProductEligibility =
            product?.regularChildSkus?.length > 0
                ? product?.regularChildSkus.some(element => element?.isReplenishmentEligible)
                : isReplenishmentEligible;
        const isAutoReplenishmentEnabled = Sephora.configurationSettings.isAutoReplenishmentEnabled;
        const isAutoReplenishEmptyHubEnabled = !!Sephora.configurationSettings?.isAutoReplenishEmptyHubEnabled;
        const showAutoReplenishment = autoReplenProductEligibility && isAutoReplenishmentEnabled;
        const isSameDayShippingEnabled = Sephora.configurationSettings.isSameDayShippingEnabled;
        // isNDDNewDeliveryName Feature flag takes precedence over API-provided sameDayTitle
        const sameDayTitleText = isNDDNewDeliveryName() ? getText('fastDelivery') : sameDayTitle || getText('sameDayDelivery');
        const sddRadioButtonDisabled = shippingMethodNotAvailable || serviceUnavailable;
        const getItShippedTitle = shouldDisplayEdd && shipToHome?.shipToHomeTitle ? shipToHome.shipToHomeTitle : getText('getItShippedTitle');

        const getItShippedSubTitle = shouldDisplayEdd && shipToHome?.shipToHomeMessage ? shipToHome.shipToHomeMessage : '';
        const { autoReplenishSubTitle, autoReplenishTitle, outOfStockAtStore, ...restTextResources } = textResources;
        const autoReplenishSubTitleText = stringUtils.format(autoReplenishSubTitle, Math.ceil(replenishmentAdjuster));
        const autoReplenishHotDeal = localeUtils.isFrench() ? 'Aubaine Autoprovision' : 'Auto-Replenish Hot Deal';
        const autoReplenishTitleText = acceleratedPromotion ? autoReplenishHotDeal : autoReplenishTitle;
        const subTitleAfterText = acceleratedPromotion ? getText('autoReplenishTermsApply') : '';
        const signInText = getText('signIn');
        const forFreeShippingText = getText('forFreeShipping');
        const freeShippingOnOrdersText = getText('freeShippingOnOrders');
        const location = Location.getLocation();
        const initialDeliveryOption = (urlUtils.getParamsByName(QUERY_PARAMS.DELIVERY_OPTION, location.search) || [])[0];
        const shouldSelectAutoReplenish =
            isAutoReplenishEmptyHubEnabled && isReplenishmentEligible && initialDeliveryOption === DELIVERY_OPTIONS.AUTO_REPLENISH.toLowerCase();
        const nearbyAvailabilityStatus = currentSku.actionFlags?.nearbyAvailabilityStatus;
        const isInStockAtNearbyStore = ExtraProductDetailsUtils.isinStock(nearbyAvailabilityStatus);
        const isOutOfStockWithNearbyInStockAlternative = isMultiStoreFilteringEnabled && isOutOfStock && isInStockAtNearbyStore;
        const alternativeSubTitle = isOutOfStockWithNearbyInStockAlternative ? outOfStockAtStore([storeName]) : null;

        const standardDeliveryOption = {
            deliveryOption: DELIVERY_OPTIONS.STANDARD,
            iconName: 'truck',
            title: userUtils.isAnonymous() ? getItShippedTitle : getText('freeShipping'),
            subTitle: getItShippedSubTitle
        };
        const autoReplenishDeliveryOption = {
            deliveryOption: DELIVERY_OPTIONS.AUTO_REPLENISH,
            iconName: 'autoReplenish',
            title: autoReplenishTitleText,
            subTitle: `${autoReplenishSubTitleText}${subTitleAfterText}`,
            hideDeliveryOption: !showAutoReplenishment,
            bccMediaSpecInfo: 'autoReplenish',
            disabled: !isReplenishmentEligible
        };
        const sameDayDeliveryOption = {
            deliveryOption: DELIVERY_OPTIONS.SAME_DAY,
            title: sameDayTitleText,
            subTitle: sameDaySubTitle,
            enableMarkdownSubtitle: true,
            iconName: 'bag',
            bccMediaSpecInfo: 'sameDay',
            hideDeliveryOption: !isSameDayShippingEnabled,
            disabled: sddRadioButtonDisabled
        };

        const DELIVERY_OPTIONS_ARRAY = [standardDeliveryOption, autoReplenishDeliveryOption, sameDayDeliveryOption];

        const bopisDeliveryOption = {
            deliveryOption: DELIVERY_OPTIONS.PICKUP,
            title: bopisTitle,
            subTitle: currentSku.isPickUpEligibleSku ? bopisCutoffSubTitle : bopisSubTitle,
            desktopSubTitle: bopisSubTitle,
            alternativeSubTitle,
            enableMarkdownSubtitle: isOutOfStockWithNearbyInStockAlternative,
            applyGreen: isOutOfStockWithNearbyInStockAlternative ? false : undefined,
            isOutOfStockWithNearbyInStockAlternative,
            iconName: 'store',
            disabled: !currentSku.isPickUpEligibleSku,
            preferredStoreInfo: preferredStoreInfo,
            currentProduct: product,
            hideDeliveryOption: !displayBopis,
            bccMediaSpecInfo: 'bopisStore'
        };

        if (reorderFulfillmentOptionsPdp) {
            DELIVERY_OPTIONS_ARRAY.unshift(bopisDeliveryOption);
        } else {
            DELIVERY_OPTIONS_ARRAY.push(bopisDeliveryOption);
        }

        if (reorderFulfillmentOptions.challengerOne) {
            DELIVERY_OPTIONS_ARRAY[0] = bopisDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[1] = standardDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[2] = sameDayDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[3] = autoReplenishDeliveryOption;
        }

        if (reorderFulfillmentOptions.challengerTwo) {
            DELIVERY_OPTIONS_ARRAY[0] = standardDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[1] = bopisDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[2] = sameDayDeliveryOption;
            DELIVERY_OPTIONS_ARRAY[3] = autoReplenishDeliveryOption;
        }

        return {
            ...restTextResources,
            basket,
            shippingMethodNotAvailable,
            isUserSduTrialEligible,
            sddRadioButtonDisabled,
            deliveryOptions: DELIVERY_OPTIONS_ARRAY,
            isSDUAddedToBasket,
            hasPickupMessage,
            signInText,
            forFreeShippingText,
            freeShippingOnOrdersText,
            isFreeShippingCopyPDPActive,
            shouldSelectAutoReplenish,
            initialDeliveryOption,
            reorderFulfillmentOptionsPdp
        };
    }
);

const functions = dispatch => ({
    showMediaModal: (...args) => {
        const action = showMediaModal(...args);
        dispatch(action);
    },
    triggerModalOpenAnalytics: isTrialAlreadyAdded => {
        SameDayUnlimitedBindings.trialModalOpen(isTrialAlreadyAdded);
    },
    showSignInModal: (...args) => {
        const action = showSignInModal(...args);
        dispatch(action);
    }
});

const withDeliveryOptionsProps = wrapHOC(connect(fields, functions));

export {
    fields, withDeliveryOptionsProps
};
